import { calendarService } from "./CalendarService";
import { driveService } from "./DriveService";
import { emailService } from "./EmailService";
import { apiCall, auth } from "./firebase";
import { googleAuthService } from "./googleAuth";

export interface GoogleServiceStatus {
  isConnected: boolean;
  lastChecked: Date;
  error?: string;
  hasPermissions: boolean;
}

export interface GoogleServicesStatus {
  calendar: GoogleServiceStatus;
  drive: GoogleServiceStatus;
  email: GoogleServiceStatus;
  overall: {
    isConnected: boolean;
    lastChecked: Date;
    connectedServices: string[];
  };
}

export interface GoogleEvent {
  id: string;
  summary: string;
  start: string;
  end: string;
  description?: string;
  location?: string;
}

export interface GoogleDriveFile {
  id: string;
  name: string;
  mimeType: string;
  size?: string;
  modifiedTime: string;
  webViewLink?: string;
}

export interface GoogleEmail {
  id: string;
  subject: string;
  from: string;
  snippet: string;
  date: string;
  isRead: boolean;
}

class GoogleServicesService {
  private lastForceCheck = 0;
  private readonly FORCE_CHECK_DEBOUNCE = 2000; // 2 seconds debounce
  private isChecking = false; // Prevent concurrent checks
  private servicesStatus: GoogleServicesStatus = {
    calendar: {
      isConnected: false,
      lastChecked: new Date(0), // Initialize to epoch to indicate never checked
      hasPermissions: false,
    },
    drive: {
      isConnected: false,
      lastChecked: new Date(0), // Initialize to epoch to indicate never checked
      hasPermissions: false,
    },
    email: {
      isConnected: false,
      lastChecked: new Date(0), // Initialize to epoch to indicate never checked
      hasPermissions: false,
    },
    overall: {
      isConnected: false,
      lastChecked: new Date(0), // Initialize to epoch to indicate never checked
      connectedServices: [],
    },
  };

  private checkInterval: NodeJS.Timeout | null = null;
  private readonly CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

  constructor() {
    this.startPeriodicChecks();
  }

  // Start periodic connection status checks
  startPeriodicChecks(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }

    this.checkInterval = setInterval(async () => {
      // Use forceCheck(false) to respect debounce for periodic checks
      await this.forceCheck(false);
    }, this.CHECK_INTERVAL);

    console.log("GoogleServicesService: Started periodic connection checks");
  }

  // Stop periodic connection checks
  stopPeriodicChecks(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
      console.log("GoogleServicesService: Stopped periodic connection checks");
    }
  }

  // Check all Google services status
  async checkAllServicesStatus(): Promise<GoogleServicesStatus> {
    // Prevent concurrent checks
    if (this.isChecking) {
      console.log("GoogleServicesService: checkAllServicesStatus already running, waiting...");
      // Wait for current check to complete
      while (this.isChecking) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      console.log("GoogleServicesService: Previous check completed, returning cached status");
      return this.servicesStatus;
    }

    this.isChecking = true;
    console.log("GoogleServicesService: Checking all services status...");

    try {
      // Check if user is authenticated with Firebase (not Google OAuth directly)
      console.log("GoogleServicesService: Checking Firebase auth...");
      const currentUser = auth.currentUser;
      console.log("GoogleServicesService: Firebase currentUser:", currentUser ? "exists" : "null");
      if (!currentUser) {
        console.log("GoogleServicesService: No Firebase user, returning unauthenticated status");
        this.updateOverallStatus(false, "User not authenticated");
        return this.servicesStatus;
      }

      // Initialize googleAuthService once before checking all services to ensure frontend user is set
      // This ensures all service checks have access to the initialized googleAuthService
      console.log("GoogleServicesService: Initializing googleAuthService before service checks...");
      try {
        await googleAuthService.initialize();
        console.log("GoogleServicesService: googleAuthService initialized successfully");
      } catch (error) {
        console.warn("GoogleServicesService: Failed to initialize googleAuthService:", error);
        // Continue anyway - individual service checks will handle initialization if needed
      }

      // Check each service - run sequentially to avoid race conditions with backend checks
      console.log("GoogleServicesService: Starting service status checks...");

      console.log("GoogleServicesService: About to call checkCalendarStatus()...");
      await this.checkCalendarStatus();
      console.log("GoogleServicesService: Calendar check completed");

      console.log("GoogleServicesService: About to call checkDriveStatus()...");
      await this.checkDriveStatus();
      console.log("GoogleServicesService: Drive check completed");

      console.log("GoogleServicesService: About to call checkEmailStatus()...");
      await this.checkEmailStatus();
      console.log("GoogleServicesService: Email check completed");

      // Update overall status
      this.updateOverallStatus();

      return this.servicesStatus;
    } finally {
      this.isChecking = false;
      console.log("GoogleServicesService: checkAllServicesStatus completed");
    }
  }

  // Check calendar service status
  private async checkCalendarStatus(): Promise<void> {
    try {
      // Check if user has a connected calendar provider
      const provider = await calendarService.getCurrentProvider();
      if (!provider || !provider.isConnected) {
        this.servicesStatus.calendar = {
          isConnected: false,
          lastChecked: new Date(),
          hasPermissions: false,
          error: "No calendar provider connected",
        };
        return;
      }

      // For Google provider, check backend auth status instead of frontend
      // Handle both 'type' and 'providerType' fields (backend uses providerType, frontend uses type)
      const providerWithType = provider as { type?: string; providerType?: string };
      const providerType = providerWithType.type || providerWithType.providerType;
      console.log("GoogleServicesService: Provider type:", providerType, "Full provider:", provider);
      if (providerType === "google" || providerType === "Google") {
        try {
          // Check backend Google auth status - need both connected AND hasRefreshToken
          const response = await apiCall("/getGoogleAuthStatus");
          console.log("GoogleServicesService: getGoogleAuthStatus response:", response);

          const hasValidTokens = response.connected && response.hasRefreshToken;
          console.log("GoogleServicesService: Calendar hasValidTokens:", hasValidTokens, { connected: response.connected, hasRefreshToken: response.hasRefreshToken });

          if (!hasValidTokens) {
            this.servicesStatus.calendar = {
              isConnected: false,
              lastChecked: new Date(),
              hasPermissions: false,
              error: response.connected && !response.hasRefreshToken ? "Google OAuth tokens are incomplete. Please reconnect." : "Google OAuth not authenticated on backend",
            };
            console.log("GoogleServicesService: Calendar status set to NOT CONNECTED:", this.servicesStatus.calendar);
            return;
          }
        } catch (error) {
          console.error("GoogleServicesService: Error checking Google auth status:", error);
          this.servicesStatus.calendar = {
            isConnected: false,
            lastChecked: new Date(),
            hasPermissions: false,
            error: "Failed to check backend Google auth status",
          };
          return;
        }

        // If we get here, backend Google auth is connected with valid tokens
        console.log("GoogleServicesService: Calendar status set to CONNECTED");
        this.servicesStatus.calendar = {
          isConnected: true,
          lastChecked: new Date(),
          hasPermissions: true,
          error: undefined,
        };
        return;
      }

      // Non-Google provider - use provider's isConnected status
      console.log("GoogleServicesService: Non-Google provider, using provider.isConnected:", provider.isConnected);
      this.servicesStatus.calendar = {
        isConnected: provider.isConnected,
        lastChecked: new Date(),
        hasPermissions: true,
        error: undefined,
      };
    } catch (error) {
      console.error("Calendar status check failed:", error);
      this.servicesStatus.calendar = {
        isConnected: false,
        lastChecked: new Date(),
        hasPermissions: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  // Check drive service status
  private async checkDriveStatus(): Promise<void> {
    try {
      // Check backend Google auth status - need both connected AND hasRefreshToken
      try {
        const response = await apiCall("/getGoogleAuthStatus");
        const hasValidTokens = response.connected && response.hasRefreshToken;

        if (!hasValidTokens) {
          this.servicesStatus.drive = {
            isConnected: false,
            lastChecked: new Date(),
            hasPermissions: false,
            error: response.connected && !response.hasRefreshToken ? "Google OAuth tokens are incomplete. Please reconnect." : "Google OAuth not authenticated on backend",
          };
          return;
        }
      } catch (error) {
        this.servicesStatus.drive = {
          isConnected: false,
          lastChecked: new Date(),
          hasPermissions: false,
          error: "Failed to check backend Google auth status",
        };
        return;
      }

      // Note: googleAuthService is initialized at the start of checkAllServicesStatus()
      // Attempt Drive API call - will fail if tokens are invalid or permissions are missing
      const status = await driveService.forceCheck();
      this.servicesStatus.drive = {
        isConnected: status.isConnected,
        lastChecked: status.lastChecked,
        hasPermissions: status.hasPermissions,
        error: status.error,
      };
    } catch (error) {
      console.error("Drive status check failed:", error);
      this.servicesStatus.drive = {
        isConnected: false,
        lastChecked: new Date(),
        hasPermissions: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  // Check email service status
  private async checkEmailStatus(): Promise<void> {
    console.log("GoogleServicesService: checkEmailStatus() STARTED");
    try {
      // Check backend Google auth status - need both connected AND hasRefreshToken
      console.log("GoogleServicesService: Email check - calling /getGoogleAuthStatus...");
      try {
        const response = await apiCall("/getGoogleAuthStatus");
        console.log("GoogleServicesService: Email check - getGoogleAuthStatus response:", JSON.stringify(response, null, 2));
        const hasValidTokens = response.connected && response.hasRefreshToken;
        console.log("GoogleServicesService: Email check - hasValidTokens:", hasValidTokens, {
          connected: response.connected,
          hasRefreshToken: response.hasRefreshToken,
        });

        if (!hasValidTokens) {
          const errorMessage = response.connected && !response.hasRefreshToken ? "Google OAuth tokens are incomplete. Please reconnect." : "Google OAuth not authenticated on backend";
          console.log("GoogleServicesService: Email check - tokens invalid, setting error:", errorMessage);
          this.servicesStatus.email = {
            isConnected: false,
            lastChecked: new Date(),
            hasPermissions: false,
            error: errorMessage,
          };
          console.log("GoogleServicesService: checkEmailStatus() ENDED - tokens invalid");
          return;
        }
        console.log("GoogleServicesService: Email check - backend tokens valid, proceeding to Gmail API check");
      } catch (error) {
        console.error("GoogleServicesService: Email check - getGoogleAuthStatus error:", error);
        this.servicesStatus.email = {
          isConnected: false,
          lastChecked: new Date(),
          hasPermissions: false,
          error: "Failed to check backend Google auth status",
        };
        console.log("GoogleServicesService: checkEmailStatus() ENDED - API error");
        return;
      }

      // Note: googleAuthService is initialized at the start of checkAllServicesStatus()
      // Attempt Gmail API call - will fail if tokens are invalid or permissions are missing
      console.log("GoogleServicesService: Email check - calling emailService.forceCheck()...");
      const status = await emailService.forceCheck();
      console.log("GoogleServicesService: Email check - emailService.forceCheck() returned:", {
        isConnected: status.isConnected,
        hasPermissions: status.hasPermissions,
        error: status.error,
      });
      this.servicesStatus.email = {
        isConnected: status.isConnected,
        lastChecked: status.lastChecked,
        hasPermissions: status.hasPermissions,
        error: status.error,
      };
      console.log("GoogleServicesService: checkEmailStatus() ENDED - success");
    } catch (error) {
      console.error("GoogleServicesService: Email status check failed with exception:", error);
      this.servicesStatus.email = {
        isConnected: false,
        lastChecked: new Date(),
        hasPermissions: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
      console.log("GoogleServicesService: checkEmailStatus() ENDED - exception");
    }
  }

  // Update overall status
  private updateOverallStatus(forceConnected?: boolean, error?: string): void {
    const connectedServices = [];

    if (this.servicesStatus.calendar.isConnected) connectedServices.push("Calendar");
    if (this.servicesStatus.drive.isConnected) connectedServices.push("Drive");
    if (this.servicesStatus.email.isConnected) connectedServices.push("Email");

    this.servicesStatus.overall = {
      isConnected: forceConnected !== undefined ? forceConnected : connectedServices.length > 0,
      lastChecked: new Date(),
      connectedServices,
    };

    if (error) {
      console.log("GoogleServicesService: Overall status error:", error);
    }
  }

  // Get current services status
  getServicesStatus(): GoogleServicesStatus {
    return { ...this.servicesStatus };
  }

  // Request Google services permissions
  async requestPermissions(): Promise<void> {
    try {
      console.log("GoogleServicesService: Requesting Google services permissions...");
      await googleAuthService.requestCalendarPermissions();
    } catch (error) {
      console.error("GoogleServicesService: Failed to request permissions:", error);
      throw error;
    }
  }

  // Clear Google auth and re-authenticate
  async clearAndReauthenticate(): Promise<void> {
    try {
      console.log("GoogleServicesService: Clearing auth and re-authenticating...");
      await googleAuthService.clearAndReauthenticate();
    } catch (error) {
      console.error("GoogleServicesService: Failed to clear and re-authenticate:", error);
      throw error;
    }
  }

  // Get calendar events
  async getCalendarEvents(maxResults = 10): Promise<GoogleEvent[]> {
    try {
      console.log("GoogleServicesService: Fetching calendar events...");

      // Check if user has a connected provider
      const hasProvider = await calendarService.hasConnectedProvider();
      if (!hasProvider) {
        console.log("GoogleServicesService: No calendar provider connected");
        return [];
      }

      const events = await calendarService.getUpcomingEvents(maxResults);

      // Convert to GoogleEvent format for backward compatibility
      const googleEvents: GoogleEvent[] = events.map((event) => ({
        id: event.id,
        summary: event.title,
        start: event.startTime.toISOString(),
        end: event.endTime.toISOString(),
        description: event.description,
        location: event.location,
      }));

      console.log(`GoogleServicesService: Fetched ${googleEvents.length} calendar events`);
      return googleEvents;
    } catch (error) {
      console.error("GoogleServicesService: Failed to fetch calendar events:", error);
      throw error;
    }
  }

  // Get all calendar events for debug screen
  async getAllCalendarEvents(): Promise<GoogleEvent[]> {
    try {
      console.log("GoogleServicesService: Fetching ALL calendar events for debug...");

      // Check if user has a connected provider
      const hasProvider = await calendarService.hasConnectedProvider();
      if (!hasProvider) {
        console.log("GoogleServicesService: No calendar provider connected");
        return [];
      }

      // Fetch up to 100 events for debug
      const events = await calendarService.getEvents({
        limit: 100,
      });

      const googleEvents: GoogleEvent[] = events.map((event) => ({
        id: event.id,
        summary: event.title,
        start: event.startTime.toISOString(),
        end: event.endTime.toISOString(),
        description: event.description,
        location: event.location,
      }));
      return googleEvents;
    } catch (error) {
      console.error("GoogleServicesService: Failed to fetch all calendar events:", error);
      throw error;
    }
  }

  // Get drive files
  async getDriveFiles(maxResults = 10): Promise<GoogleDriveFile[]> {
    try {
      console.log("GoogleServicesService: Fetching drive files...");
      const files = await driveService.getRecentFiles(maxResults);

      // Convert to GoogleDriveFile format for backward compatibility
      const googleFiles: GoogleDriveFile[] = files.map((file) => {
        // Ensure modifiedTime is a valid string
        let modifiedTime = file.modifiedTime;
        if (!modifiedTime || typeof modifiedTime !== "string") {
          console.warn("GoogleServicesService: Invalid modifiedTime for file:", file.name, file.modifiedTime);
          modifiedTime = new Date().toISOString(); // Fallback to current time
        }
        return {
          id: file.id,
          name: file.name,
          mimeType: file.mimeType,
          size: file.size,
          modifiedTime: modifiedTime,
          webViewLink: file.webViewLink,
        };
      });

      console.log(`GoogleServicesService: Fetched ${googleFiles.length} drive files`);
      return googleFiles;
    } catch (error) {
      console.error("GoogleServicesService: Failed to fetch drive files:", error);
      throw error;
    }
  }

  // Get emails
  async getEmails(maxResults = 10): Promise<GoogleEmail[]> {
    try {
      console.log("GoogleServicesService: Fetching emails...");
      const emails = await emailService.getEmails(maxResults);

      // Convert to GoogleEmail format for backward compatibility
      const googleEmails: GoogleEmail[] = emails.map((email) => ({
        id: email.id,
        subject: email.subject,
        from: email.from,
        snippet: email.snippet,
        date: email.date,
        isRead: email.isRead,
      }));

      console.log(`GoogleServicesService: Fetched ${googleEmails.length} emails`);
      return googleEmails;
    } catch (error) {
      console.error("GoogleServicesService: Failed to fetch emails:", error);
      throw error;
    }
  }

  // Force check all services with debouncing
  async forceCheck(bypassDebounce = false): Promise<GoogleServicesStatus> {
    const now = Date.now();
    const stackTrace = new Error().stack;
    console.log(`GoogleServicesService: forceCheck called (bypassDebounce: ${bypassDebounce})`, {
      timestamp: new Date().toISOString(),
      lastForceCheck: this.lastForceCheck,
      timeSinceLastCheck: now - this.lastForceCheck,
      isChecking: this.isChecking,
      stackTrace: stackTrace?.split("\n").slice(1, 4).join("\n"),
    });

    // If a check is already running, wait for it to complete (regardless of bypassDebounce)
    if (this.isChecking) {
      console.log("GoogleServicesService: Check already in progress, waiting for completion...");
      while (this.isChecking) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
      console.log("GoogleServicesService: Previous check completed, returning status");
      return this.servicesStatus;
    }

    // CRITICAL: Only check debounce if bypassDebounce is false
    // If bypassDebounce is true, we MUST always perform a fresh check
    if (!bypassDebounce) {
      if (now - this.lastForceCheck < this.FORCE_CHECK_DEBOUNCE) {
        console.log("GoogleServicesService: Force check debounced, returning cached status");
        return this.servicesStatus;
      }
    }

    // Always update timestamp and perform fresh check
    this.lastForceCheck = now;
    console.log(`GoogleServicesService: Force checking all services... (bypassDebounce: ${bypassDebounce})`);

    try {
      const freshStatus = await this.checkAllServicesStatus();
      console.log("GoogleServicesService: Force check completed, status:", {
        calendar: freshStatus.calendar.isConnected,
        drive: freshStatus.drive.isConnected,
        email: freshStatus.email.isConnected,
        emailError: freshStatus.email.error,
      });
      return freshStatus;
    } catch (error) {
      console.error("GoogleServicesService: Force check failed with error:", error);
      // Return current status even on error
      return this.servicesStatus;
    }
  }

  // Cleanup when service is no longer needed
  destroy(): void {
    this.stopPeriodicChecks();
    console.log("GoogleServicesService: Destroyed");
  }
}

// Create singleton instance
export const googleServicesService = new GoogleServicesService();
